import { useState } from 'react';
import { GenericEventsRouteProvider } from '@wix/data-tools-generic-events';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { Me } from '../../../../lib/me';
import { useBasePath } from '../../../../lib/use-base-path';
import { markNavFromDrawer } from '../../../../lib/drawer-nav';
import { useLeaveChatOnNavigate } from '../../../../lib/use-leave-chat';
import { useUnreadNotifications } from '../../../../lib/use-unread-notifications';
import { useTranslation } from '../../../../lib/i18n';
import { Avatar, Button, IconButton, ProgressBar } from '../../ui';
import { EarnCreditsOffer } from '../../earn-credits/EarnCreditsOffer';
import { BellIcon } from './icons';
import { useAccountSummary } from '../hooks/use-account-summary';
import { useCreditsSummary } from '../hooks/use-credits-summary';

// The WIDE rail's account card: the credits card above an account row (avatar +
// name + the notifications bell). The avatar opens Settings — the web analog of
// mobile's Menu → Settings — and carries the hidden brand-toggle long press.
// Who it shows and what it computes lives in useAccountSummary / useCreditsSummary;
// the mobile drawer renders the same models with its own markup.
export function AccountCard({ me, collapsed = false }: { me: Me | null; collapsed?: boolean }) {
  const { t } = useTranslation();
  const basePath = useBasePath();
  const leaveChat = useLeaveChatOnNavigate();
  const router = useRouter();
  const { initials, displayName, email, photoUrl, onPhotoError, settings } = useAccountSummary(me);
  const [hovered, setHovered] = useState(false);

  // Reserve the card's height before `me` resolves so the rail doesn't jump.
  if (!initials) {
    return <div style={{ minHeight: 36 }} />;
  }

  return (
    <div className="orion-account-outer" style={{ position: 'relative' }}>
      <div
        className="orion-account-card"
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 8,
          padding: 0,
          background: 'transparent',
          borderRadius: 'var(--sym-radius-container)',
        }}
      >
        {/* Credits card sits above the account row (Figma order) */}
        <span className="orion-account-credits orion-rail-label">
          <GenericEventsRouteProvider value="credits">
            <CreditsCard />
          </GenericEventsRouteProvider>
        </span>

        {/* Account row: in expanded → [avatar btn] [bell] horizontal.
            In collapsed → [bell] on top, [avatar btn] below (column-reverse),
            both 36px wide and left-aligned to sit within the icon-strip clip. */}
        <div style={{
          display: 'flex',
          flexDirection: collapsed ? 'column-reverse' : 'row',
          alignItems: collapsed ? 'flex-start' : 'center',
          gap: collapsed ? 16 : 4,
        }}>
          <button
            type="button"
            onClick={() => {
              settings.onClick();
              void router.push(settings.path);
            }}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            aria-label={displayName || email}
            title={displayName || email}
            className="orion-account-btn"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: collapsed ? 0 : 12,
              flex: collapsed ? undefined : 1,
              width: collapsed ? 36 : undefined,
              minWidth: 0,
              minHeight: 36,
              paddingBlock: 2,
              paddingInlineStart: collapsed ? 0 : 4,
              paddingInlineEnd: collapsed ? 0 : 8,
              justifyContent: collapsed ? 'center' : undefined,
              borderRadius: 8,
              border: 'none',
              background: hovered && !collapsed ? 'var(--ds-surface-standard-bg)' : 'transparent',
              cursor: 'pointer',
              textAlign: 'start',
              fontFamily: 'inherit',
              transition: 'background-color 0.15s ease',
            }}
          >
            {/* Circular avatar — the shared <Avatar>: photo, else initials over the gradient. */}
            <Avatar initials={initials} photoUrl={photoUrl} onPhotoError={onPhotoError} size={30} />
            <span className="orion-rail-label" style={{ minWidth: 0 }}>
              <span
                style={{
                  display: 'block',
                  fontSize: 14,
                  fontWeight: 'var(--sym-weight-semibold)',
                  color: 'var(--ds-text-primary)',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {displayName}
              </span>
            </span>
          </button>
          {/* Bell icon — moved from the standalone NotificationsBell row into the account row */}
          <BellButton basePath={basePath} leaveChat={leaveChat} label={t('nav.notifications')} collapsed={collapsed} />
        </div>
      </div>
    </div>
  );
}

// Bell icon button — links to notifications, shows the unread dot if there are any.
function BellButton({
  basePath,
  leaveChat,
  label,
  collapsed,
}: {
  basePath: string;
  leaveChat: () => void;
  label: string;
  collapsed: boolean;
}) {
  const unreadCount = useUnreadNotifications();
  const router = useRouter();
  const linkProps = {
    href: '/notifications',
    'aria-label': label,
    'aria-current': router.pathname === '/notifications' ? ('page' as const) : undefined,
    onClick: () => {
      markNavFromDrawer();
      leaveChat();
    },
  };
  const dot = unreadCount > 0 && (
    <span
      aria-hidden
      style={{
        position: 'absolute',
        top: collapsed ? 1 : 6,
        insetInlineEnd: collapsed ? 1 : 6,
        width: 8,
        height: 8,
        borderRadius: '50%',
        background: 'var(--sym-peach)',
        pointerEvents: 'none',
      }}
    />
  );
  if (collapsed) {
    return (
      <Link
        {...linkProps}
        as={`${basePath}/notifications`}
        title={label}
        className="orion-nav-item"
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 36,
          padding: '6px 0',
          borderRadius: 'var(--ds-radius-8)',
          textDecoration: 'none',
        }}
      >
        <span
          aria-hidden
          className="orion-nav-icon"
          style={{ position: 'relative', width: 24, height: 24, flexShrink: 0, borderRadius: 7, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--ds-icon-primary)' }}
        >
          <BellIcon />
          {dot}
        </span>
      </Link>
    );
  }
  return (
    <span style={{ position: 'relative', display: 'inline-flex', flexShrink: 0 }}>
      <IconButton {...linkProps} linkAs={`${basePath}/notifications`} size="medium">
        <BellIcon />
      </IconButton>
      {dot}
    </span>
  );
}

// Credits card — plan name + Upgrade button + credit count + progress bar.
// The card body opens Account; the Upgrade button opens the package picker (the same
// destination as the Account page's Upgrade). They must not nest — an <a> inside an <a>
// is invalid — so the body is a stretched <Link> behind the content, and the content
// is click-through (`pointer-events: none`) except for the Upgrade button.
function CreditsCard() {
  const credits = useCreditsSummary();
  if (!credits) return null;

  return (
    <div style={{ position: 'relative', borderRadius: 16 }}>
      <Link
        href={credits.account.route}
        as={credits.account.path}
        onClick={credits.account.onClick}
        aria-label={credits.account.label}
        title={credits.title}
        style={{ position: 'absolute', inset: 0, borderRadius: 16, zIndex: 0 }}
      />
      <div
        style={{
          position: 'relative',
          zIndex: 1,
          pointerEvents: 'none',
          display: 'flex',
          flexDirection: 'column',
          gap: 16,
          padding: 16,
          borderRadius: 16,
          background: 'var(--ds-surface-standard-bg)',
          color: 'inherit',
        }}
      >
        {/* Plan name + Upgrade button. Upgrade stays the primary action and keeps its place; the
            earn-credits offer (red band only, behind its own gate) is a secondary control beside it.
            The button is offered on every plan: neither native card gates it on free/paid either, and
            both hide only the whole card (on `creditsCap <= 0`, which is our "no meter" case).
            `upgrade.label` is what says Upgrade vs Switch. Parity here is the GATE, not the
            destination — native picks between the Symphony picker and the Wixel page itself (behind a
            confirmation), where web always opens /packages and that page makes the same choice. */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
          <span style={{ fontSize: 16, fontWeight: 'var(--sym-weight-semibold)', letterSpacing: '-0.04em', lineHeight: '20px', color: 'var(--ds-text-primary)', whiteSpace: 'nowrap' }}>
            {credits.planLabel}
          </span>
          <span style={{ flexShrink: 0, display: 'inline-flex', alignItems: 'center', gap: 8, pointerEvents: 'auto' }}>
            <EarnCreditsOffer entry={{ kind: 'sidebar_credits', fraction: credits.fraction }} pageName="menu" />
            <Button
              size="small"
              href={credits.upgrade.route}
              linkAs={credits.upgrade.path}
              onClick={credits.upgrade.onClick}
            >
              {credits.upgrade.label}
            </Button>
          </span>
        </div>
        {/* Credit count + progress bar */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {/* `.orion-sidebar` sets nowrap for the wide rail's collapse clip, so this
              sentence has to opt back out or it is clipped rather than wrapped — it is a
              full sentence carrying two numbers, and the longest translations run ~35%
              past the English. Same treatment, for the same reason, as the mobile
              drawer's `.usageLabel`. */}
          <ProgressBar
            progress={credits.progress}
            value={credits.fraction * 100}
            label={credits.usageLabel}
            decorative
          />
        </div>
      </div>
    </div>
  );
}
